import { useTranslation } from "react-i18next";

/**
 * Elegir un día: flecha atrás, calendario, flecha adelante.
 *
 * ── Por qué es un componente y no una copia ────────────────────────────────
 * Vivía dentro de `ConduccionPanel` y Evidencias lo necesitaba igual. Copiarlo
 * habría dado dos selectores en el MISMO modal que se ven parecidos y se
 * comportan distinto en cuanto uno de los dos reciba un arreglo — y el usuario
 * tendría que aprenderse cuál es cuál según la pestaña.
 *
 * ── Fechas como texto, sin `Date` ──────────────────────────────────────────
 * Todo se maneja en `YYYY-MM-DD`. Convertir a `Date` para sumar un día mete la
 * zona horaria del navegador en una decisión que es del calendario: a las 19:00
 * de México, `new Date("2026-09-27")` ya es el 28 en UTC, y la flecha saltaría
 * dos días o ninguno según la hora a la que alguien la toque.
 */

/** Hoy en `YYYY-MM-DD`, en la zona de quien mira. `en-CA` da justo ese formato. */
export function hoy(): string {
  return new Date().toLocaleDateString("en-CA");
}

/** Suma días a un `YYYY-MM-DD` sin tocar zonas horarias. */
export function correr(fecha: string, dias: number): string {
  const [y, m, d] = fecha.split("-").map(Number);
  if (!y || !m || !d) return fecha;
  const base = new Date(Date.UTC(y, m - 1, d));
  base.setUTCDate(base.getUTCDate() + dias);
  return base.toISOString().slice(0, 10);
}

export interface SelectorDeDiaProps {
  /** El día mostrado, en `YYYY-MM-DD`. */
  dia: string;
  onDia: (fecha: string) => void;
  /**
   * Hasta dónde se puede avanzar. Por omisión, hoy.
   *
   * No se deja pasar del día de hoy: no hay evidencia del futuro, y un
   * calendario que permite pedirlo devuelve un vacío que parece un fallo.
   */
  max?: string;
}

export default function SelectorDeDia({ dia, onDia, max = hoy() }: SelectorDeDiaProps) {
  const { t } = useTranslation("common");

  const anterior = t("monitoreoIa.conduccion.diaAnterior", { defaultValue: "Día anterior" });
  const siguiente = t("monitoreoIa.conduccion.diaSiguiente", { defaultValue: "Día siguiente" });

  return (
    <div className="flex items-center gap-1.5 sm:gap-1">
      <button
        type="button"
        onClick={() => onDia(correr(dia, -1))}
        title={anterior}
        aria-label={anterior}
        className="flex shrink-0 items-center justify-center rounded-lg border border-outline/40 px-1.5 py-1 text-content-secondary transition-colors hover:bg-surface max-sm:h-9 max-sm:w-9"
      >
        <i className="ri-arrow-left-s-line" aria-hidden="true" />
      </button>

      <input
        type="date"
        value={dia}
        max={max}
        onChange={(e) => onDia(e.target.value)}
        aria-label={t("monitoreoIa.conduccion.fecha", { defaultValue: "Fecha" })}
        className="min-w-0 flex-1 rounded-lg border border-outline/40 bg-surface px-2 py-1 text-xs tabular-nums text-content sm:flex-none"
      />

      <button
        type="button"
        onClick={() => onDia(correr(dia, 1))}
        disabled={dia >= max}
        title={siguiente}
        aria-label={siguiente}
        className="flex shrink-0 items-center justify-center rounded-lg border border-outline/40 px-1.5 py-1 text-content-secondary transition-colors hover:bg-surface disabled:opacity-40 disabled:hover:bg-transparent max-sm:h-9 max-sm:w-9"
      >
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
      </button>
    </div>
  );
}
